// Copyright (c) 2021 WiseTime. All rights reserved.

import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "app/store"
import { TrashStatus } from "generated/wisetime/user/timeline/v4/timeline_pb"
import { TimeRowsCacheUpdate } from "../slices/timeline"
import { ActiveTrashMonthAndDate, getActiveTrashMonthAndDateFromPath, TimelineDate, TimelineMonth, } from "features/timeline/dateTime"
import { notUndefined } from "lib/utils"
import { BASE_PATH, TRASH_BASE_PATH } from "app/routing/paths"
import { setTimeRowsForDates } from "features/timeline/slices/timeline"
import { TimeRowsCache } from "../types"

export type TrashedTimeRowsState = ActiveTrashMonthAndDate & {
  activeTrashedTimeRowIds: string[],
  trashedRowsRefreshRequired: boolean,
  emptyTrashDoNotAskAgain: boolean,
}

export const getInitialActiveMonthAndDate = (): ActiveTrashMonthAndDate => {
  // the URL path may contain hash e.g. trash/2021-03#01, hence use `location.hash`
  const slashAtStart = 1
  const pathnameWithHash = location.pathname + location.hash
  const monthParamWithHash = pathnameWithHash.substring(
    slashAtStart + BASE_PATH.length + TRASH_BASE_PATH.length,
    pathnameWithHash.length,
  )
  return getActiveTrashMonthAndDateFromPath(monthParamWithHash)
}

export const initialState: TrashedTimeRowsState = {
  ...getInitialActiveMonthAndDate(),
  activeTrashedTimeRowIds: [],
  trashedRowsRefreshRequired: true,
  emptyTrashDoNotAskAgain: false,
}

export const slice = createSlice({
  name: "trash",
  initialState,
  reducers: {
    setActiveTrashMonth: (
      state,
      action: PayloadAction<ActiveTrashMonthAndDate>,
    ): void => {
      state.activeTrashDate = action.payload.activeTrashDate
      state.activeTrashMonth = action.payload.activeTrashMonth
      state.trashedRowsRefreshRequired = true
      state.activeTrashedTimeRowIds = []
    },
    resetActiveTrashDate: (state) : void => {
      state.activeTrashDate = undefined
    },
    addActiveTrashedTimeRowIds: (state, action: PayloadAction<string[]>): void => {
      const timeRowIds = action.payload
      const { activeTrashedTimeRowIds } = state
      timeRowIds.forEach(timeRowId => {
        if (!activeTrashedTimeRowIds.includes(timeRowId)) {
          activeTrashedTimeRowIds.push(timeRowId)
        }
      })
    },
    removeActiveTrashedTimeRowIds: (state, action: PayloadAction<string[]>): void => {
      const timeRowIds = action.payload
      const { activeTrashedTimeRowIds } = state
      timeRowIds.forEach(timeRowId => {
        const foundIndex = activeTrashedTimeRowIds.findIndex(x => x == timeRowId)
        if (foundIndex > -1) {
          activeTrashedTimeRowIds.splice(foundIndex, 1)
        }
      })
      state.activeTrashedTimeRowIds = activeTrashedTimeRowIds
    },
    resetActiveTrashedTimeRowIds: (state): void => {
      state.activeTrashedTimeRowIds = initialState.activeTrashedTimeRowIds
    },
    setTrashedRowsRefreshRequired: (state, action: PayloadAction<boolean>): void => {
      state.trashedRowsRefreshRequired = action.payload
    },
    setEmptyTrashDoNotAskAgain: (state, action: PayloadAction<boolean>) => {
      state.emptyTrashDoNotAskAgain = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setTimeRowsForDates, (state, action: PayloadAction<TimeRowsCacheUpdate>): void => {
        if (action.payload.ofTrashStatus === TrashStatus.TRASHED) {
          state.trashedRowsRefreshRequired = false
        }
      })
  },
})

export const {
  setActiveTrashMonth,
  resetActiveTrashDate,
  addActiveTrashedTimeRowIds,
  removeActiveTrashedTimeRowIds,
  resetActiveTrashedTimeRowIds,
  setTrashedRowsRefreshRequired,
  setEmptyTrashDoNotAskAgain,
} = slice.actions

export const selectActiveTrashedTimeRowIds = (state: RootState): string[] => state.trash.activeTrashedTimeRowIds

export const selectActiveTrashedTimeRows = createSelector(
  selectActiveTrashedTimeRowIds,
  (state: RootState): TimeRowsCache => state.timeline.timeRowsCache,
  (activeTrashedTimeRowIds, timeRowsCache) => {
    return activeTrashedTimeRowIds
      .filter(timeRowId => timeRowId !== undefined && timeRowId !== "")
      .map(timeRowId => timeRowsCache[timeRowId])
      .filter(notUndefined)
  }
)

export const selectActiveTrashDate = (state: RootState): TimelineDate | undefined => state.trash.activeTrashDate

export const selectActiveTrashMonth = (state: RootState): TimelineMonth => state.trash.activeTrashMonth

export const selectTrashedRowsRefreshRequired = (state: RootState): boolean => state.trash.trashedRowsRefreshRequired

export const selectEmptyTrashDoNotAskAgain = (state: RootState): boolean => state.trash.emptyTrashDoNotAskAgain

export default slice.reducer
